/*
  <capra-care-form>: the care forms' shared base. One module, four forms
  (Customizations, Warranty & repairs, the Bespoke commission, and Returns &
  exchanges, in capra-order-form.js, its own component). The page's form is
  Shopify's own contact form, so a request is never lost; this script sends it
  to capra-app instead, and steps aside whenever capra-app does not answer:
    1. shows the piece the visitor was looking at (?product=, ?variant=);
    2. uploads files straight to storage through short-lived links from
       /apps/capra/uploads (files never pass through the proxy);
    3. posts the fields marked data-care to /apps/capra/care with the form's
       intent and the file keys;
    4. on a network error or a 5xx, lets the native form post go through, naming the files it
       could not carry.
  A 409, 422 or 429 is a real answer and is shown, not retried; a file over the limit is said at
  once, and the visitor keeps the form and the files. Every call to
  capra-app carries theme_role (from the page's theme.role).
  With data-steps the form asks one question at a time; without this script
  every question is on the page.
*/
import { Refused, UPLOAD_LIMITS, send, upload } from './capra.js';

// Markets live under a folder (/en-de/): never fetch a fixed root path.
const ROOT = window.Shopify?.routes?.root || '/';
const params = new URLSearchParams(location.search);

export class CapraCareForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.intent = this.dataset.intent;
    this.themeRole = this.dataset.themeRole; // Liquid theme.role (not named `role`: that is the element's ARIA role attribute): a preview must never be stored as a real request
    this.context = {
      product_handle: params.get('product') || '',
      variant_id: params.get('variant') || '',
      colour: '',
      page_url: params.get('product') ? `/products/${params.get('product')}` : '',
    };
    this.setContext('page', this.context.page_url);
    this.setContext('product', this.context.product_handle);
    this.setContext('variant', this.context.variant_id);
    this.showPiece();
    this.initSteps();
    this.form.addEventListener('submit', (event) => this.submit(event));
  }

  setContext(name, value) {
    const field = this.form.querySelector(`[data-context="${name}"]`);
    if (field && value) field.value = value;
  }

  /** Name and photo of the piece, so the customer sees what the desk will see. */
  async showPiece() {
    const handle = this.context.product_handle;
    const box = this.querySelector('[data-piece]');
    if (!handle || !box) return;
    try {
      const response = await fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return;
      const product = await response.json();
      const variant = product.variants.find((v) => String(v.id) === this.context.variant_id);
      const option = variant && product.variants.length > 1 ? variant.title : '';
      this.context.colour = product.title.includes(' · ') ? product.title.split(' · ').pop() : '';
      this.setContext('product', product.title);
      this.setContext('variant', option);
      const label = document.createElement('p');
      label.textContent = [product.title, option].filter(Boolean).join(' · ');
      box.replaceChildren();
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = product.featured_image.replace(/(\.[a-z]+)(\?|$)/i, '_160x$1$2');
        image.alt = '';
        image.width = 80;
        image.height = 80;
        box.append(image);
      }
      box.append(label);
      box.hidden = false;
    } catch {
      /* the handle stays in the hidden field; the desk can still find the piece */
    }
  }

  /** One question at a time: every [data-step] but the current one is hidden. */
  initSteps() {
    this.steps = [...this.form.querySelectorAll('[data-step]')];
    if (!this.hasAttribute('data-steps') || this.steps.length < 2) return;
    this.index = 0;
    const submit = this.form.querySelector('[type="submit"]');
    const nav = document.createElement('div');
    nav.className = 'care__nav';
    this.back = this.button(this.dataset.back, () => this.go(-1));
    this.next = this.button(this.dataset.next, () => this.go(1));
    submit.before(nav);
    nav.append(this.back, this.next, submit);
    this.nav = nav;
    this.form.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('input:not([type="file"])') && this.index < this.steps.length - 1) {
        event.preventDefault();
        this.go(1);
      }
    });
    this.show();
  }

  button(text, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button';
    button.textContent = text;
    button.addEventListener('click', onClick);
    return button;
  }

  go(direction) {
    if (direction > 0) {
      const fields = [...this.steps[this.index].querySelectorAll('input, textarea, select')];
      if (!fields.every((field) => field.reportValidity())) return;
    }
    this.index = Math.min(Math.max(this.index + direction, 0), this.steps.length - 1);
    this.show();
  }

  show() {
    this.steps.forEach((step, i) => (step.hidden = i !== this.index));
    const last = this.index === this.steps.length - 1;
    this.back.hidden = this.index === 0;
    this.next.hidden = last;
    this.form.querySelector('[type="submit"]').hidden = !last;
    this.steps[this.index].querySelector('input:not([type="hidden"]), textarea, select')?.focus({ preventScroll: true });
  }

  async submit(event) {
    if (this.sendingNative || !this.dataset.careUrl) return; // no capra-app link: Shopify's form takes it
    event.preventDefault();
    if (!this.form.reportValidity()) return;
    const button = this.form.querySelector('[type="submit"]');
    const message = this.form.querySelector('[data-message]');
    const say = (text) => { message.textContent = text; message.hidden = false; };
    const files = [...this.form.querySelectorAll('[data-files]')].flatMap((input) => [...input.files]);
    const big = files.find((file) => file.size > UPLOAD_LIMITS.bytes);
    if (big) return say(this.dataset.fileLarge.replace('__NAME__', big.name));
    if (files.length > UPLOAD_LIMITS.files) return say(this.dataset.fileCount);
    const label = button.textContent;
    button.disabled = true;
    button.textContent = this.dataset.sending;
    message.hidden = true;
    try {
      const keys = await upload(this.dataset.uploadsUrl, files, this.themeRole);
      const { response, answer } = await send(this.dataset.careUrl, this.payload(keys), this.themeRole);
      if (response.ok) return this.done();
      say(answer.error || this.dataset.slow);
    } catch (error) {
      if (error instanceof Refused) {
        say(error.message || this.dataset.fileFailed); // capra-app answered: the visitor keeps the form and the files
      } else {
        this.native(files); // capra-app is down or unreachable: Shopify's form takes it
        return;
      }
    }
    button.disabled = false;
    button.textContent = label;
  }

  /** Shopify's form takes the request; files cannot travel with it, so it names them for the desk. */
  native(files) {
    if (files.length) {
      const note = Object.assign(document.createElement('input'), { type: 'hidden', name: 'contact[Files not sent]' });
      note.value = files.map((file) => file.name).join(', ');
      this.form.append(note);
    }
    this.sendingNative = true;
    this.form.requestSubmit();
  }

  /** The fields marked data-care, under the names the care endpoint expects. */
  payload(keys) {
    const data = new FormData();
    data.set('intent', this.intent);
    for (const field of this.form.querySelectorAll('[data-care]')) {
      if ((field.type === 'radio' || field.type === 'checkbox') && !field.checked) continue;
      if (field.disabled || field.closest('[hidden]') || field.value === '') continue;
      data.append(field.dataset.care, field.value);
    }
    for (const [key, value] of Object.entries(this.context)) if (value) data.set(key, value);
    for (const key of keys) data.append('files', key);
    return data;
  }

  done() {
    const done = document.createElement('p');
    done.className = 'care__done';
    done.setAttribute('role', 'status');
    done.textContent = this.dataset.thanks;
    this.form.replaceWith(done);
  }
}

if (!customElements.get('capra-care-form')) customElements.define('capra-care-form', CapraCareForm);
